import { activeJobsFor, enqueue, markApplied, promiseFor, takePending } from '~/lib/uploadqueue'
const FileManagerModal = defineAsyncComponent(() => import('~/components/modals/FileManagerModal.vue'))

// Mixin for editors whose images are uploaded through lib/uploadqueue.js.
//
// The editor provides:
// - computed `uploadOwner`: { type, id, label, open } for a saved entity, where
//   `open` is the showDetailsColumn message that reopens the editor
// - method `applyUploadResult(slot, file, meta)`: put an uploaded file
//   ({ id, filename, ext }) into the edited entity
// and calls:
// - `await this.awaitUploadsBeforeSave()` in save(), before setting `saving`
//   and collecting the save data
// - `this.applyPendingUploads()` when loading the entity has finished
// and may override `handlesUploadSlot(slot)` when it shows only some of the
// owner's images (StorefrontSettings and CompanyProfile share the settings)
//
// Results that arrive while the editor saves or loads are held back and
// applied after the reload, because the reload replaces the local state.
// Results for an editor that is closed stay in the queue until it is opened.
export default {
  data () {
    return {
      waitingForUploads: 0,
      deferredUploads: []
    }
  },
  computed: {
    uploadOwnerKey () {
      const owner = this.uploadOwner
      if (!owner || !owner.type) {
        return null
      }
      // Unsaved entities get a key of their own for as long as the editor is open
      return this.shopId + ':' + owner.type + ':' + (owner.id ? owner.id : 'new-' + this.$.uid)
    },
    ownUploads () {
      return this.uploadOwnerKey ? this.$store.state.uploads.filter(u => u.owner === this.uploadOwnerKey) : []
    }
  },
  methods: {
    queueUpload (source, slot, { name = '', meta = null } = {}) {
      const owner = this.uploadOwner || {}
      return enqueue(source, {
        owner: this.uploadOwnerKey,
        ownerLabel: owner.label || '',
        open: owner.id ? owner.open || null : null,
        slot,
        name,
        meta
      })
    },
    queueUploads (files, slot) {
      return Array.from(files || []).map(file => this.queueUpload(file, slot))
    },
    // Pick images that are already in the shop's files; they need no upload
    chooseFromLibrary (slot, { multiple = false, max = 0 } = {}) {
      this.showModal('filemanager', FileManagerModal, {
        props: {
          pickMode: multiple ? 'multiple' : 'single',
          maxPick: max
        },
        width: 1000,
        events: {
          pick: (files) => {
            for (const file of files) {
              this.applyUploadResult(slot, file, null)
            }
          }
        }
      })
    },
    onUploadCompleted (message) {
      if (!message || !this.uploadOwnerKey || message.owner !== this.uploadOwnerKey || !this.handlesUploadSlot(message.slot)) {
        return
      }
      markApplied(message.jobId)
      if (this.saving || this.loading) {
        this.deferredUploads.push(message)
        return
      }
      this.applyUploadResult(message.slot, message.file, message.meta)
    },
    handlesUploadSlot () {
      return true
    },
    async awaitUploadsBeforeSave () {
      if (!this.uploadOwnerKey) {
        return
      }
      let active = activeJobsFor(this.uploadOwnerKey)
      while (active.length) {
        this.waitingForUploads = active.length
        await Promise.allSettled(active.map(u => promiseFor(u.id)))
        active = activeJobsFor(this.uploadOwnerKey)
      }
      this.waitingForUploads = 0
    },
    applyPendingUploads () {
      const deferred = this.deferredUploads
      this.deferredUploads = []
      for (const message of deferred) {
        if (message.owner === this.uploadOwnerKey) {
          this.applyUploadResult(message.slot, message.file, message.meta)
        } else {
          // The editor now shows another entity, so the upload waits for its own one
          markApplied(message.jobId, false)
        }
      }
      if (!this.uploadOwnerKey) {
        return
      }
      for (const pending of takePending(this.uploadOwnerKey, slot => this.handlesUploadSlot(slot))) {
        this.applyUploadResult(pending.slot, pending.file, pending.meta)
      }
    }
  },
  mounted () {
    this.EventBus.$on('uploadCompleted', this.onUploadCompleted)
  },
  beforeUnmount () {
    this.EventBus.$off('uploadCompleted', this.onUploadCompleted)
    for (const message of this.deferredUploads) {
      markApplied(message.jobId, false)
    }
    this.deferredUploads = []
  }
}
